import { useEffect, useState } from "react";
import CampaignPageHeader from "../components/CampaignPageHeader";
import { getParticipantRecord } from "@api/creator_view";
import { useNavigate, useParams } from "react-router-dom";
import LoadingSpinner from "@components/LoadingSpinner";
import { capitalize } from "lodash";
import { LazyRouteContent } from "@components/navigation/LazyRouteContent";
import BriefPreviewContent from "../brief-preview/BriefPreviewContent";
import ProfileContentReview from "./ProfileContentReview";
import { ParticipantRecord } from "types/creator-view";
import { Participant, Campaign } from "@types";
import { useMixpanelTracking } from "@hooks/useMixpanelTracking";
import { EVENTS } from "@utils/mixpanel_utilities";

import FButton from "@components/FButton";
import BgCoverImage from "../brief-preview/bgCoverImage";

import SubmitDeliverablesAlert from "./components/SubmitDeliverablesAlert";

const CreatorCampaignPage = () => {
  const { subpage } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [participantRecord, setParticipantRecord] =
    useState<ParticipantRecord>(null);
  const { participant_id } = useParams();
  const participantId = parseInt(participant_id, 10);
  const { trackEvent, trackButtonClick } = useMixpanelTracking();
  const [activeTab, setActiveTab] = useState(subpage || "brief");

  useEffect(() => {
    const loadCampaign = async () => {
      try {
        const response = await getParticipantRecord(participantId);
        const { name, status, campaign } = response.data;

        const participant = {
          ...response.data,
          creator: { firstName: name, full_name: name },
          id: participantId,
          status,
        };

        setParticipantRecord(participant);

        // Set document title
        document.title = `Campaign: ${campaign?.title || "View Campaign"}`;

        // Track campaign view for influencer
        trackEvent(EVENTS.CAMPAIGN.VIEWED, {
          campaign_id: campaign?.id,
          campaign_name: campaign?.title,
          participant_id: participantId,
          status: status,
          view_type: "influencer",
        });
      } catch (error) {
        console.error("Error loading campaign:", error);

        // Track error
        trackEvent("Error", {
          error_type: "Campaign Load Error",
          participant_id: participantId,
          error_message: error?.message || "Unknown error",
        });
      } finally {
        setLoading(false);
      }
    };

    loadCampaign();
  }, [participantId, trackEvent]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);

    const trackingParams = {
      campaign_id: participantRecord?.campaign?.id,
      campaign_name: participantRecord?.campaign?.title,
      participant_id: participantId,
    };

    // Track tab change
    trackButtonClick("Campaign Tab", {
      tab_name: capitalize(tab),
      ...trackingParams,
    });

    // Update URL
    if (subpage) {
      navigate(`../${tab}`, { relative: "path" });
    } else {
      navigate(tab);
    }

    trackEvent(
      tab === "content"
        ? EVENTS.PAGE_VIEW.CONTENT_SUBMISSION
        : EVENTS.PAGE_VIEW.CAMPAIGN_BRIEF_VIEWED,
      trackingParams
    );
  };

  // Track content submission view on initial load if that's the selected tab
  useEffect(() => {
    if (loading) {
      return;
    }

    const trackingParams = {
      campaign_id: participantRecord.campaign?.id,
      campaign_name: participantRecord.campaign?.title,
      participant_id: participantId,
    };

    trackEvent(
      activeTab === "content"
        ? EVENTS.PAGE_VIEW.CONTENT_SUBMISSION
        : EVENTS.PAGE_VIEW.CAMPAIGN_BRIEF_VIEWED,
      trackingParams
    );
  }, [loading, activeTab, participantRecord, trackEvent, participantId]);

  if (loading) {
    return <LoadingSpinner className="w-full h-[90vh]" />;
  }

  const { campaign, brief } = participantRecord;

  // Create a safe campaign object with all required properties to avoid errors
  const campaignWithBrief = {
    ...campaign,
    brief: brief || {
      cover_image: { original: null, large: null },
      sections: [],
      description: "",
    },
  } as Campaign;

  // Create a participant object from participantRecord
  const participant = participantRecord as unknown as Participant;

  const handleGoToContent = () => {
    handleTabChange("content");
    // Wait for content tab to be active before scrolling to deliverables section
    setTimeout(() => {
      const deliverablesSectionElement = document.getElementById(
        "your-deliverables-title"
      );
      if (deliverablesSectionElement) {
        deliverablesSectionElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  return (
    <div className="relative z-[99]">
      <SubmitDeliverablesAlert
        participant={participantRecord}
        onGoToSubmit={handleGoToContent}
      />
      <BgCoverImage
        imageUrl={brief?.cover_image?.original || brief?.cover_image?.large}
      />
      <div className="relative z-10 c-lg:px-[64px] px-[20px] -mt-[400px]">
        <div className="mb-[0px] pb-[14px] flex flex-row justify-end w-full pt-[22px] mb-[22px] h-[48px]">
          {activeTab === "brief" && (
            <FButton
              onClick={() => {
                // Track CTA click
                trackButtonClick("Complete Deliverable CTA", {
                  campaign_id: participantRecord?.campaign?.id,
                  campaign_name: participantRecord?.campaign?.title,
                  participant_id: participantId,
                  from_tab: activeTab,
                });

                // Navigate to content tab
                handleTabChange("content");
              }}
              primary
              label="Upload Content"
              iconLeft={{
                name: "add",
                className: "-mt-[1px] mr-[4px]",
                size: 12,
                color: "white",
              }}
              className="h-[48px] px-6 shadow-md"
              aria-label="Complete Deliverable"
            />
          )}
        </div>
        <div className="mb-[50px]">
          <CampaignPageHeader
            centered
            campaign={campaignWithBrief}
            participant={participant}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </div>

        {/* Content Area */}
        <div className="mb-[40px]">
          <div className="">
            <LazyRouteContent isActive={activeTab === "brief"}>
              <BriefPreviewContent
                campaign={{
                  ...campaignWithBrief,
                  participants: [participant],
                }}
                className="p-0 m-0"
                hideHeader
                participant_id={participantId}
              />
            </LazyRouteContent>

            <LazyRouteContent isActive={activeTab === "content"}>
              <ProfileContentReview participantRecord={participantRecord} />
            </LazyRouteContent>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorCampaignPage;
